// MUST BE FIRST - loads runtime config before any other code
// supports reading from window.ENV
import './runtime-config';

import '@i18n/i18n';
import './styles/app-base.css';
import './styles/app-components.css';
import './styles/app-utilities.css';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import routes from '@/configs/routesConfig';
import { Provider } from 'react-redux';
import store from '@/store/store';
import { ViewerProvider } from '@/contexts/ViewerContext';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
// Note: Syncfusion's heavy ej2-base is intentionally NOT imported here. The license
// is registered lazily in initializeSyncfusion() (see configs/syncfusionConfig.ts) so
// ej2-base stays out of the app's entry chunk and only loads on pages that use it.

// Determine if we should run MSW
// Mocks are opt-in. Local Docker development has a real WebAPI behind Vite's
// /api proxy, so enabling MSW merely intercepts those real requests.
const useMockApi = import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true';

// Note: Syncfusion license is registered via initializeSyncfusion() which fetches from backend
// See syncfusionConfig.ts - the license key comes from appsettings.json -> Syncfusion:LicenseKey
(window as any).pdfWorkerPath = '/ej2-pdfviewer-lib/';

async function mockSetup() {
  if (useMockApi) {
    try {
      const { worker } = await import('@utils/mswMockAdapter');
      return worker
        .start({
          onUnhandledRequest: 'bypass',
          serviceWorker: {
            url: `/mockServiceWorker.js` // ✅ Use relative path, no API_BASE_URL
          }
        })
        .catch((error) => {
          console.error('❌ MSW Failed to start:', error);
          return Promise.resolve();
        });
    } catch (error) {
      console.error('❌ Failed to import MSW:', error);
      return Promise.resolve();
    }
  }

  // Remove a worker left behind by an earlier mock-enabled dev session; service
  // workers survive reloads and would otherwise keep intercepting the real API.
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations
        .filter((registration) => registration.active?.scriptURL.includes('/mockServiceWorker.js'))
        .map((registration) => registration.unregister())
    );
  }

  return Promise.resolve();
}

/**
 * The root element of the application.
 */
const container = document.getElementById('app');

if (!container) {
  throw new Error('Failed to find the root element');
}

// Start MSW only in development mode
mockSetup().then(() => {
  /**
   * The root component of the application.
   */
  const root = createRoot(container);
  const router = createBrowserRouter(routes);

  const theme = createTheme({
    palette: {
      mode: 'light'
    }
  });

  root.render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Provider store={store}>
          <ViewerProvider>
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </ViewerProvider>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
});
